import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { VIEW_ONLY_FAVORITES_TABS } from 'src/constants/plans.constants'
import { ROUTES } from 'src/lib/routes'
import { IModifiedPlan } from 'src/types/modifiedPlan.interface'

import {
  addFavoriteBasePlans,
  FavoriteBasePlansParams,
  getFavoriteBasePlans,
  getFavoritePlans as getFavorites,
  removeFavoriteBasePlans,
} from '../actions/get-favorite-plans'

const determineInitialTab = (): string => {
  const url = location.pathname

  if (url.includes(ROUTES.FAVORITES_SHELLS)) {
    return VIEW_ONLY_FAVORITES_TABS.SHELL_PLANS
  } else if (url.includes(ROUTES.FAVORITES_UNITS)) {
    return VIEW_ONLY_FAVORITES_TABS.UNIT_PLANS
  } else {
    return VIEW_ONLY_FAVORITES_TABS.SINGLE_FAMILY_PLANS
  }
}

interface FavoriteBasePlans {
  shells: string[]
  single: string[]
  units: string[]
}

export interface FavoritePlansInitialState {
  data: IModifiedPlan[]
  error: unknown
  favoriteBasePlans: FavoriteBasePlans
  favoritesTabValue: string
  loading: boolean
}

const initialState: FavoritePlansInitialState = {
  data: [],
  error: null,
  favoriteBasePlans: {} as FavoriteBasePlans,
  favoritesTabValue: determineInitialTab(),
  loading: false,
}

export const getFavoritePlans = createAsyncThunk(
  'favorite/plans',
  async (email: string) => {
    return await getFavorites(email)
  }
)

export const getAllFavoriteBasePlans = createAsyncThunk(
  'favorites/base-plans',
  async (email: string) => {
    return await getFavoriteBasePlans(email)
  }
)

export const addFavoriteBasePlan = createAsyncThunk(
  'favorite/base-plans',
  async (params: FavoriteBasePlansParams) => {
    return await addFavoriteBasePlans(params)
  }
)

export const removeFavoriteBasePlan = createAsyncThunk(
  'unfavorite/base-plans',
  async (params: FavoriteBasePlansParams) => {
    return await removeFavoriteBasePlans(params)
  }
)

export const FavoritePlansSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(getFavoritePlans.pending, (state) => {
        state.loading = true
        state.error = ''
      })
      .addCase(getFavoritePlans.fulfilled, (state, action) => {
        state.loading = false
        state.data = action.payload as unknown as IModifiedPlan[]
        state.error = ''
      })
      .addCase(getFavoritePlans.rejected, (state, action) => {
        state.loading = false
        state.data = []
        state.error = action.error.message ?? 'Error fetching favorite plans'
      })
      .addCase(addFavoriteBasePlan.pending, (state) => {
        state.loading = true
        state.error = ''
      })
      .addCase(addFavoriteBasePlan.fulfilled, (state, action) => {
        state.loading = false
        state.favoriteBasePlans = action.payload as unknown as FavoriteBasePlans
        state.error = ''
      })
      .addCase(addFavoriteBasePlan.rejected, (state, action) => {
        state.loading = false
        state.data = []
        state.error =
          action.error.message ?? 'Error adding base plan to favorites'
      })
      .addCase(removeFavoriteBasePlan.pending, (state) => {
        state.loading = true
        state.error = ''
      })
      .addCase(removeFavoriteBasePlan.fulfilled, (state, action) => {
        state.loading = false
        state.favoriteBasePlans = action.payload as unknown as FavoriteBasePlans
        state.error = ''
      })
      .addCase(removeFavoriteBasePlan.rejected, (state, action) => {
        state.loading = false
        state.data = []
        state.error =
          action.error.message ?? 'Error removing base plan from favorites'
      })
      .addCase(getAllFavoriteBasePlans.pending, (state) => {
        state.loading = true
        state.error = ''
      })
      .addCase(getAllFavoriteBasePlans.fulfilled, (state, action) => {
        state.loading = false
        state.favoriteBasePlans = action.payload as unknown as FavoriteBasePlans
        state.error = ''
      })
      .addCase(getAllFavoriteBasePlans.rejected, (state, action) => {
        state.loading = false
        state.data = []
        state.error =
          action.error.message ?? 'Error removing base plan from favorites'
      })
  },
  initialState,
  name: 'favoritePlans',
  reducers: {
    setSelectedFavoritesTab: (state, action: PayloadAction<string>) => {
      state.favoritesTabValue = action.payload
    },
  },
})

export const { setSelectedFavoritesTab } = FavoritePlansSlice.actions
